import _ from 'lodash';

import * as autoDirectorsActions from '../components/FullName/limitedCompany/AutoDirectors.actions';
import * as businessIntroductionDEActions from '../containers/BusinessIntroductionDE/BusinessIntroductionDE.actions';
import { deserializeApplicationState } from '../helpers/applicationManager';
import applicationSchema from '../helpers/applicationSchema';
import applicationSchemaDE from '../helpers/applicationSchemaDE';

const UK = 'uk';

const schemas = {
    uk: applicationSchema,
    de: applicationSchemaDE
};

export const initialState = {
    application: {
        payload: schemas[UK],
        success: null,
        fetching: false,
        error: null,
        failedAction: null,
        loading: false
    },
    companies: {
        list: [],
        fetching: false,
        success: null
    },
    directors: {
        list: [],
        fetching: false,
        success: null
    },
    legalFormDE: {
        payload: '',
        fetching: false,
        success: null
    },
    addresses: {
        list: [],
        fetching: false,
        success: null
    },
    documentRequirements: {
        payload: [],
        fetching: false,
        success: null
    },
    documentRequirementStatus: {
        fetching: false,
        success: null,
        payload: {
            files: [],
            accounts: []
        }
    },
    intercomAttributes: {},
    decision: {
        payload: {}
    },
    checkAdditionalGuarantors: {
        payload: {}
    },
    productApplication: {
        payload: {}
    }
};

function setApplicationLoadingState(state, { loading }) {
    return {
        ...state,
        application: {
            ...state.application,
            loading
        }
    };
}

function updateIntercomAttributes(state, intercomAttributes = {}) {
    return {
        ...state,
        intercomAttributes: _.merge(
            {},
            state.intercomAttributes,
            intercomAttributes
        )
    };
}

function setApplicationInitialStateOnSuccess(
    state,
    { application, countryCode = UK }
) {
    return {
        ...state,
        application: {
            ...state.application,
            payload: _.merge(
                {},
                schemas[countryCode],
                deserializeApplicationState(application)
            ),
            fetching: false,
            success: true,
            error: null
        }
    };
}

function setApplicationStateOnFail(state, { failedAction, code }) {
    return {
        ...state,
        application: {
            ...state.application,
            payload: schemas[UK],
            fetching: false,
            success: false,
            error: {
                time: new Date().toISOString(),
                action: failedAction,
                code
            }
        }
    };
}

function setApplicationOnCustomerSaveSuccess(state) {
    return {
        ...state,
        application: {
            ...state.application,
            fetching: false,
            success: true,
            error: null
        }
    };
}

function setAddressesOnSuccess(state, addresses) {
    return {
        ...state,
        addresses: {
            ...state.addresses,
            list: addresses,
            fetching: false,
            success: true
        }
    };
}

function setAddressesOnFail(state) {
    return {
        ...state,
        addresses: {
            ...state.addresses,
            list: [],
            fetching: false,
            success: false
        }
    };
}

function setCompaniesOnSuccess(state, companies) {
    return {
        ...state,
        companies: {
            ...state.companies,
            list: companies,
            fetching: false,
            success: true
        }
    };
}

function clearFetchedCompaniesList(state) {
    return {
        ...state,
        companies: {
            ...state.companies,
            list: [],
            fetching: false,
            success: null
        }
    };
}

function setCompaniesOnFail(state) {
    return {
        ...state,
        companies: {
            ...state.companies,
            list: [],
            fetching: false,
            success: false
        }
    };
}

function setCompaniesFetchRequested(state) {
    return {
        ...state,
        companies: {
            ...state.companies,
            fetching: true
        }
    };
}

function setAddressFetchRequested(state) {
    return {
        ...state,
        addresses: {
            ...state.addresses,
            fetching: true
        }
    };
}

function setDirectorsFetchRequested(state) {
    return {
        ...state,
        directors: {
            ...state.directors,
            fetching: true
        }
    };
}

function setDirectorsOnSuccess(state, directors) {
    return {
        ...state,
        directors: {
            ...state.directors,
            list: directors,
            fetching: false,
            success: true
        }
    };
}

function setDirectorsOnFail(state) {
    return {
        ...state,
        directors: {
            ...state.directors,
            list: [],
            fetching: false,
            success: false
        }
    };
}

function removeGuarantor(state, uid) {
    return {
        ...state,
        application: {
            ...state.application,
            payload: {
                ...state.application.payload,
                people: state.application.payload.people.filter(
                    (person) => person.uid !== uid
                )
            }
        }
    };
}

function setGetDecision(state, payload) {
    return {
        ...state,
        decision: {
            ...state.decision,
            payload
        }
    };
}

function setCheckAdditionalGuarantors(state, payload) {
    return {
        ...state,
        checkAdditionalGuarantors: {
            ...state.checkAdditionalGuarantors,
            payload
        }
    };
}

export default function (state = initialState, action) {
    switch (action.type) {
        case 'INITIAL_APP_START':
            return setApplicationLoadingState(state, { loading: true });
        case 'INITIAL_APP_END':
            return setApplicationLoadingState(state, { loading: false });
        case 'APPLICATION_STATE_FETCH_SUCCEED':
            return setApplicationInitialStateOnSuccess(state, action.payload);
        case 'SAVE_CUSTOMER_STATE_SUCCEED':
            return setApplicationOnCustomerSaveSuccess(state);
        case 'APPLICATION_STATE_FETCH_FAILED':
        case 'SAVE_CUSTOMER_FAILED':
            return setApplicationStateOnFail(state, action.payload);
        case 'COMPANIES_FETCH_REQUESTED':
        case businessIntroductionDEActions.COMPANIES_FETCH_REQUESTED_DE:
            return setCompaniesFetchRequested(state);
        case businessIntroductionDEActions.RESET_FETCHED_COMPANIES:
            return { ...state, companies: initialState.companies };
        case 'COMPANIES_FETCH_SUCCEED':
            return setCompaniesOnSuccess(state, action.companies);
        case 'COMPANIES_FETCH_FAILED':
            return setCompaniesOnFail(state);
        case 'CLEAR_FETCHED_COMPANIES_LIST':
            return clearFetchedCompaniesList(state);
        case 'ADDRESSES_FETCH_REQUESTED':
            return setAddressFetchRequested(state);
        case 'ADDRESSES_FETCH_SUCCEED':
            return setAddressesOnSuccess(state, action.addresses);
        case 'ADDRESSES_FETCH_FAILED':
            return setAddressesOnFail(state);
        case 'REMOVE_GUARANTOR':
            return removeGuarantor(state, action.uid);
        case 'UPDATE_INTERCOM_ATTRIBUTES':
            return updateIntercomAttributes(state, action.attributes);
        case autoDirectorsActions.DIRECTORS_FETCH_REQUESTED:
            return setDirectorsFetchRequested(state);
        case autoDirectorsActions.DIRECTORS_FETCH_SUCCEED:
            return setDirectorsOnSuccess(state, action.directors);
        case autoDirectorsActions.DIRECTORS_FETCH_FAILED:
            return setDirectorsOnFail(state);
        case autoDirectorsActions.RESET_DIRECTORS:
            return { ...state, directors: initialState.directors };
        case 'GET_DECISION_SUCCEED':
            return setGetDecision(state, action.payload);
        case 'CHECK_ADDITIONAL_GUARANTORS_SUCCEED':
            return setCheckAdditionalGuarantors(state, action.payload);
        case 'UPDATE_REQUESTED_DURATION':
            return {
                ...state,
                application: {
                    ...state.application,
                    payload: {
                        ...state.application.payload,
                        loan_request: {
                            ...state.application.payload.loan_request,
                            term: `${action.payload}m`,
                            duration: action.payload
                        }
                    }
                }
            };
        default:
            return state;
    }
}
